/**
 * Aba "Radar" da barra lateral: liga a camada, escolhe o produto
 * (refletividade ou chuva em mm/h), mostra a legenda e, para cada radar, a
 * varredura que está na tela no quadro atual.
 */
import clsx from 'clsx';
import { CircleQuestionMark, ExternalLink, Radar, RefreshCw } from 'lucide-react';
import { useMemo } from 'react';
import { formatInt } from '../lib/format';
import { formatRelative, formatTimeBrt } from '../lib/time';
import { useMapInstance } from '../map/mapContext';
import { shownTimeOf, useTimeStore } from '../state/time';
import { useNow } from '../ui/hooks';
import { Button, Hint, ProgressBar, Section, Segmented, Slider, Toggle } from '../ui/primitives';
import { SIPAM_URL, type RadarInfo } from './api';
import { geometriaDe } from './cache';
import { avisoDeFrescor, radarCalado } from './frescor';
import { LegendaRadar } from './LegendaRadar';
import { DEF_PRODUTO, type Produto } from './produtos';
import { LIMITES_OPACIDADE, useRadarStore, type RadarNoQuadro } from './store';
import { carregarRadar } from './sync';
import { TOLERANCIA_MS } from './tempo';
import { loopLoadAllowed, useLoopStore } from '../loop/store';

const OPCOES_PRODUTO: { value: Produto; label: string }[] = [
  { value: 'dbz', label: 'Refletividade (dBZ)' },
  { value: 'rate', label: 'Chuva (mm/h)' },
];

/** Texto curto da situação de um radar no quadro exibido. */
export function situacaoDoRadar(
  q: RadarNoQuadro | undefined,
  quadro: number | null,
): { texto: string; tom: 'ok' | 'neutro' | 'alerta' } {
  if (!q) return { texto: '—', tom: 'neutro' };
  switch (q.estado) {
    case 'semDados':
      return { texto: 'sem dados no SIPAM', tom: 'neutro' };
    case 'sem':
      return { texto: 'sem varredura perto deste horário', tom: 'neutro' };
    case 'falhou':
      return { texto: 'a imagem não carregou', tom: 'alerta' };
    case 'carregando':
      return { texto: 'baixando…', tom: 'neutro' };
    case 'fora':
    case 'ok': {
      if (q.varredura === null) return { texto: '—', tom: 'neutro' };
      const hora = formatTimeBrt(q.varredura);
      const min = quadro === null ? 0 : Math.round((q.varredura - quadro) / 60_000);
      const texto = min === 0 ? hora : `${hora} (${min > 0 ? '+' : '−'}${Math.abs(min)} min)`;
      // Fora da vista: a varredura existe, só não está desenhada agora.
      return { texto, tom: q.estado === 'ok' ? 'ok' : 'neutro' };
    }
  }
}

/**
 * O quadro na tela está fora do que o espelho guarda (mais antigo que a
 * primeira varredura, ou mais novo que a última de todos os radares)?
 */
export function foraDaJanela(radares: RadarInfo[], produto: Produto, quadro: number | null): 'antes' | 'depois' | null {
  if (quadro === null) return null;
  let primeira = Infinity;
  let ultima = -Infinity;
  for (const r of radares) {
    const t = r.produtos[produto].t;
    if (!t.length) continue;
    primeira = Math.min(primeira, t[0]);
    ultima = Math.max(ultima, t[t.length - 1]);
  }
  if (!Number.isFinite(primeira)) return null;
  if (quadro < primeira - TOLERANCIA_MS) return 'antes';
  if (quadro > ultima + TOLERANCIA_MS) return 'depois';
  return null;
}

function ListaRadares(props: { radares: RadarInfo[] }) {
  const noQuadro = useRadarStore((s) => s.noQuadro);
  const produto = useRadarStore((s) => s.produto);
  const quadro = useTimeStore((s) => shownTimeOf(s));
  const map = useMapInstance((s) => s.map);
  const now = useNow(60_000);
  const irPara = (id: string) => {
    const geo = geometriaDe(id);
    if (!map || !geo) return;
    map.getView().fit(geo.extent, { padding: [40, 40, 40, 40], duration: 400 });
  };
  return (
    <ul className="divide-y divide-ink-700/60 rounded-lg border border-ink-700/70">
      {props.radares.map((r) => {
        const s = situacaoDoRadar(noQuadro[r.id], quadro);
        const t = r.produtos[produto].t;
        const ultima = t.length ? t[t.length - 1] : null;
        const calado = ultima !== null && radarCalado(ultima, now);
        return (
          <li key={r.id}>
            <button
              type="button"
              onClick={() => irPara(r.id)}
              disabled={!geometriaDe(r.id)}
              title={`Enquadrar o radar de ${r.nome}`}
              className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-xs hover:bg-ink-700/50 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-mata-400 disabled:opacity-60 pointer-coarse:py-2.5"
            >
              <span className="min-w-0 flex-1 truncate text-ink-100">{r.nome}</span>
              <span
                className={clsx(
                  'shrink-0 tabular-nums',
                  s.tom === 'ok' && 'text-rio-300',
                  s.tom === 'neutro' && 'text-ink-400',
                  s.tom === 'alerta' && 'text-alerta-300',
                )}
              >
                {s.texto}
              </span>
              {calado && (
                <span className="shrink-0 text-[10px] text-alerta-300" title="Última varredura publicada">
                  · calado há {formatRelative(ultima, now).replace(/^há /, '')}
                </span>
              )}
            </button>
          </li>
        );
      })}
    </ul>
  );
}

export function RadarPanel() {
  const visivel = useRadarStore((s) => s.visivel);
  const produto = useRadarStore((s) => s.produto);
  const opacidade = useRadarStore((s) => s.opacidade);
  const situacao = useRadarStore((s) => s.situacao);
  const manifesto = useRadarStore((s) => s.manifesto);
  const progresso = useRadarStore((s) => s.progresso);
  const { setVisivel, setProduto, setOpacidade } = useRadarStore.getState();
  const now = useNow(60_000);
  const aviso = avisoDeFrescor(manifesto?.gerado ?? null, now);
  const def = DEF_PRODUTO[produto];
  const radares = manifesto?.radares ?? [];
  const comDados = useMemo(() => radares.filter((r) => r.produtos[produto].t.length > 0).length, [radares, produto]);
  // Antes do primeiro play só a varredura do quadro na tela baixa: a barra
  // "N de M" pareceria travada, então some até o loop ser liberado.
  const loopLiberado = useLoopStore((s) => loopLoadAllowed(s));
  const baixando = loopLiberado && progresso.total > 0 && progresso.baixadas + progresso.falhas < progresso.total;
  const quadro = useTimeStore((s) => shownTimeOf(s));
  const fora = visivel ? foraDaJanela(radares, produto, quadro) : null;

  return (
    <div>
      <Section title="Radar meteorológico" icon={Radar}>
        <Toggle
          label="Chuva vista pelos radares do SIPAM"
          checked={visivel}
          onChange={setVisivel}
          description="Os ecos de chuva dos radares da Amazônia, a cada 10–12 min, animados junto com o satélite."
        />

        {visivel && aviso && (
          <div className="rounded-lg border border-alerta-400/40 bg-alerta-400/10 px-2.5 py-1.5 text-[11px] leading-snug text-alerta-200">
            {aviso}
          </div>
        )}
        {visivel && situacao.estado === 'error' && !manifesto && (
          <div className="rounded-lg border border-alerta-400/40 bg-alerta-400/10 px-2.5 py-1.5 text-[11px] leading-snug text-alerta-200">
            Não deu para baixar a lista de varreduras: {situacao.erro}
          </div>
        )}

        {fora && (
          <div className="rounded-lg border border-ink-600 bg-ink-800/60 px-2.5 py-1.5 text-[11px] leading-snug text-ink-200">
            {fora === 'antes'
              ? `Este horário é mais antigo que as últimas ${manifesto?.janelaHoras ?? 48} h que o AmaView guarda dos radares: o mapa fica sem radar.`
              : 'A varredura deste horário ainda não chegou: o SIPAM publica com 10–20 min de atraso, e o espelho atualiza a cada 15 min.'}
          </div>
        )}

        {visivel && (
          <>
            <Segmented label="Produto" value={produto} options={OPCOES_PRODUTO} onChange={setProduto} size="sm" />
            <div className="space-y-1.5">
              <div className="text-xs font-medium text-ink-200">{def.titulo}</div>
              <LegendaRadar produto={produto} />
              <Hint>{def.dica}</Hint>
            </div>
            <Slider
              label="Opacidade"
              value={opacidade}
              {...LIMITES_OPACIDADE}
              onChange={setOpacidade}
              format={(v) => `${Math.round(v * 100)}%`}
            />
            {baixando && (
              <div className="space-y-1">
                <ProgressBar value={progresso.total ? progresso.baixadas / progresso.total : 0} />
                <div className="text-[11px] text-ink-300 tabular-nums">
                  Baixando as varreduras: {formatInt(progresso.baixadas)} de {formatInt(progresso.total)}
                </div>
              </div>
            )}
          </>
        )}
      </Section>

      {visivel && radares.length > 0 && (
        <Section title="Radares no quadro atual" icon={Radar} storageKey="radar-lista">
          <ListaRadares radares={radares} />
          <Hint>
            Cada radar tem o seu relógio: uns varrem a cada 10 min, outros a cada 12. Em cada quadro aparece a varredura
            mais próxima do horário da imagem do satélite, até {Math.round(TOLERANCIA_MS / 60_000)} min antes ou depois
            — o número entre parênteses. Sem varredura nesse intervalo, o radar some naquele quadro. Toque num radar
            para enquadrá-lo e num eco para ler o valor.
          </Hint>
        </Section>
      )}

      <Section title="Dados" icon={RefreshCw} defaultOpen={false} storageKey="radar-dados">
        <div className="space-y-1.5 text-xs text-ink-300">
          {situacao.estado === 'loading' && <div>Baixando a lista de varreduras…</div>}
          {situacao.estado === 'error' && <div className="text-alerta-300">Falha ao atualizar: {situacao.erro}</div>}
          {manifesto && (
            <div>
              {formatInt(comDados)} de {formatInt(radares.length)} radares com dados · últimas {manifesto.janelaHoras} h
              {manifesto.gerado !== null && <> · espelho atualizado {formatRelative(manifesto.gerado, now)}</>}
            </div>
          )}
          <Button icon={RefreshCw} onClick={() => void carregarRadar(true)}>
            Atualizar agora
          </Button>
        </div>
      </Section>

      <Section title="O que é" icon={CircleQuestionMark} defaultOpen={false} storageKey="radar-oque">
        <div className="space-y-2 text-xs leading-relaxed text-ink-300">
          <p>
            O radar meteorológico manda pulsos de micro-ondas e mede o que volta das gotas de chuva — o <strong>eco</strong>.
            Quanto mais água no ar, mais forte o eco. Ele vê a chuva por dentro da nuvem, o que o satélite não vê: a nuvem
            pode ser enorme e chover só num canto dela.
          </p>
          <p>
            A rede do SIPAM cobre a Amazônia com radares em Belém, Boa Vista, Cruzeiro do Sul, Manaus, Porto Velho,
            Santarém, São Gabriel da Cachoeira, São Luís e Tabatinga, cada um enxergando cerca de 240 km em volta. Macapá e
            Tefé fazem parte da rede, mas hoje não publicam imagens.
          </p>
          <p>
            As imagens são as que o SIPAM publica, com as cores da legenda oficial. Para ficarem no lugar certo sobre o
            satélite, elas são reprojetadas pixel a pixel, sem misturar cores. O AmaView guarda as últimas 48 h; períodos
            mais antigos aparecem sem radar.
          </p>
          <p className="text-ink-400">
            Fonte: SIPAM — Sistema de Proteção da Amazônia (Censipam, Ministério da Defesa).{' '}
            <a
              href={SIPAM_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-0.5 text-rio-300 hover:underline"
            >
              gov.br/censipam
              <ExternalLink size={10} />
            </a>
          </p>
          <p className="text-ink-400">
            O radar também pega eco de terreno e de insetos perto da antena, e a chuva estimada em mm/h não é medida de
            pluviômetro. Não serve para alerta de desastre: para isso, a Defesa Civil.
          </p>
        </div>
      </Section>
    </div>
  );
}
